'use client';

import { type RoadMapUpdate } from '@/domain/roadmap/types';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@andritzot/metris-web-ui/feedback/dialog';
import { FormControl } from '@andritzot/metris-web-ui/inputs/form-control';
import { InputLabel } from '@andritzot/metris-web-ui/inputs/input-label';
import { Select } from '@andritzot/metris-web-ui/inputs/select';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { MenuItem } from '@andritzot/metris-web-ui/navigation/menu/menu-item';
import { useState } from 'react';
import { type Control, useController, type UseFormSetValue, useWatch } from 'react-hook-form';

interface FeatureRequestFieldProps {
  viewOnly: boolean;
  control: Control<RoadMapUpdate, unknown>;
  setValue: UseFormSetValue<RoadMapUpdate>;
}

export function FeatureRequestField({ viewOnly, control, setValue }: Readonly<FeatureRequestFieldProps>) {
  const {
    field: { value, onChange },
  } = useController({ name: 'origin', control });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState<number | null>(null);
  const externalOrigin = useWatch({ control, name: 'externalOrigin' });

  const handleChange = (newValue: number) => {
    const isChangingToInternal = newValue === 0 && value === 1 && !!externalOrigin;
    if (isChangingToInternal) {
      setPendingValue(newValue);
      setConfirmOpen(true);
    } else {
      onChange(newValue);
    }
  };

  const handleConfirm = () => {
    if (pendingValue !== null) {
      onChange(pendingValue);
      setValue('externalOrigin', '');
    }
    setConfirmOpen(false);
    setPendingValue(null);
  };

  const handleCancel = () => setConfirmOpen(false);

  return (
    <>
      <Box sx={{ py: 0.75, width: '100%' }}>
        <FormControl fullWidth size="small" disabled={viewOnly}>
          <InputLabel id="feature-request-label">Feature Request</InputLabel>
          <Select
            value={value ?? 0}
            labelId="feature-request-label"
            label="Feature Request"
            onChange={(e) => handleChange(Number(e.target.value))}
          >
            <MenuItem value={0}>Internal</MenuItem>
            <MenuItem value={1}>External</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Dialog open={confirmOpen} onClose={handleCancel} maxWidth="xs" fullWidth>
        <DialogTitle title="Feature Request" closable onClose={handleCancel} />
        <DialogContent>
          <Typography variant="body2">This change will clear the MOCX link inserted</Typography>
        </DialogContent>
        <DialogActions
          primaryAction={{ label: 'Continue and change', onClick: handleConfirm }}
          secondaryAction={{ label: 'Keep', onClick: handleCancel }}
        />
      </Dialog>
    </>
  );
}
